import { ChevronDown as ExpandMore, ChevronLeft, ChevronRight } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Accordion, AccordionDetails, AccordionSummary } from "../../../../components/ui/DataDisplay";
import { Snackbar } from "../../../../components/ui/Feedback";
import { Typography } from "../../../../components/ui/LayoutPrimitives";
import { useCart } from "../../../../context/CartContext.jsx";
import FormatPrice from "../../../../components/FormatPrice.jsx";
import { addShoppingCartItems } from "../../../../api/action/user.jsx";
const ProdDetailsContext = createContext(null);
export const ProdDetailsProvider = ProdDetailsContext.Provider;
export const useProdDetails = () => {
  return useContext(ProdDetailsContext);
};

const SFProductDetails = () => {
  const navigate = useNavigate();
  const userDetail = useSelector((state) => state?.auth?.data);
  const storedProduct = useSelector((state) => state.selectedProduct.selectedProduct);
  const [productDetails, setProductDetails] = useState("");
  const [quantity, setQuantity] = useState(1);
  const existingProducts = JSON.parse(localStorage.getItem("cartProducts"));
  const { addToCart } = useCart();

  const [alertMsg, setAlertMsg] = React.useState({
    open: false,
    message: "",
    severity: "error",
  });

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMsg({ open: false });
  };

  useEffect(() => {
    setProductDetails(storedProduct);

    return () => {
      setProductDetails([]);
    };
  }, [storedProduct]);

  const addToShoppingCart = async (product) => {
    const shoppingCartId = userDetail.account.shoppingCart.primaryId;
    if (!shoppingCartId) {
      setAlertMsg({
        message: "Shopping cart ID is not available. Please refresh the page.",
        open: true,
        severity: "error",
      });
      return;
    }

    try {
      const response = await addShoppingCartItems(shoppingCartId, product);
      if (response) {
        navigate("/cart");
      }
    } catch (error) {
      setAlertMsg({
        message: error.message,
        open: true,
        severity: "error",
      });
    }
  };

  const handleCart = async (productDetails) => {
    let isProductInCart = (existingProducts || []).some(
      (item) => item.product.primaryId === productDetails.primaryId
    );
    if (!isProductInCart) {
      let retailReq = {
        quantity: quantity,
        // totalPrice: productDetails.price.priceAmt,
        product: {
          deleted: productDetails.deleted,
          name: productDetails?.name,
          imageUrlsList: productDetails?.imageUrlsList || [],
          price: {
            priceAmt: productDetails?.price?.priceAmt,
          },
          description: productDetails?.description,
          code: productDetails.code,
          _id: productDetails._id,
          primaryId: productDetails.primaryId,
          bulkOrderEnabled: Boolean(productDetails.bulkOrderEnabled),
        },
      };
      addToShoppingCart(retailReq);
      addToCart(retailReq);
    } else {
      setAlertMsg({
        message: "Product already in the cart",
        open: true,
        severity: "error",
      });
      return;
    }
  };

  const incrementQuantity = () => {
    if (quantity === 10) {
      setAlertMsg({
        message: "Quantity cannot be more than 10",
        open: true,
        severity: "warning",
      });
      setQuantity(10);
    } else {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    if (
      productDetails?.imageContents &&
      productDetails.imageContents.length > 0
    ) {
      setMainImage(productDetails.imageContents && productDetails.imageContents[0] && productDetails.imageContents[0].displayLink);
    }
  }, [productDetails]);

  return (
    <>
      <div className="mx-auto max-w-7xl px-2 py-10 lg:px-0">
        <div className="overflow-hidden">
          <div className="mb-9 pt-4 md:px-6 md:pt-7 lg:mb-2 lg:p-8 2xl:p-10 2xl:pt-10">
            <div className="flex flex-wrap justify-start mb-4 overflow-x-auto max-w-full">
              {productDetails?.imageContents &&
                productDetails.imageContents.map((imgObj, index) => (
                  <img
                    loading="lazy"
                    key={index}
                    src={imgObj.displayLink}
                    alt={`${productDetails?.name || "Product"} thumbnail ${index + 1}`}
                    className="h-16 w-16 object-cover rounded-md m-1 border border-gray-300 shadow-sm cursor-pointer"
                    onMouseEnter={() => {
                      setMainImage(imgObj.displayLink);
                    }}
                  />
                ))}
            </div>
            <div className="items-start justify-between lg:flex lg:space-x-8">
              <div className="mb-4 items-center justify-center overflow-hidden md:mb-8 lg:mb-0 xl:flex">
                <div className="w-full xl:flex xl:flex-row-reverse">
                  <div className="relative mb-2.5 w-full shrink-0 overflow-hidden rounded-md border md:mb-3 xl:w-[480px] 2xl:w-[650px] lg:h-[468px] h-[300px]">
                    <div className="relative flex items-center justify-center h-full">
                      <img
                        loading="lazy"
                        src={mainImage}
                        alt={productDetails && productDetails.name}
                        width={650}
                        height={590}
                        className="rounded-lg object-contain md:h-[300px] md:w-full scale-75 lg:h-full"
                      />
                    </div>
                    <div className="absolute top-2/4 z-10 flex w-full items-center justify-between">
                      <ChevronLeft className="text-white" />
                      <ChevronRight className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col lg:w-[430px] xl:w-[470px] 2xl:w-[480px] capitalize">
                <div className="pb-2">
                  <h2 className="text-lg text-center font-semibold md:text-xl xl:text-2xl text-black">
                    {productDetails && productDetails.name}
                  </h2>
                  <p className="mt-4 text-center font-semibold text-xl text-[#B88E2F]">
                    {productDetails.price && (
                      <FormatPrice
                        code={productDetails.price.currency || "USD"}
                        price={productDetails.price.priceAmt}
                      />
                    )}
                  </p>
                </div>

                {/* Quantity Selector */}
                <div className="space-s-4 3xl:pr-48 flex items-center gap-6 border-b border-gray-300 py-8 md:pr-32 lg:pr-12 2xl:pr-12">
                  <div className="group flex h-11 flex-shrink-0 items-center justify-between overflow-hidden rounded-md border-2 border-gray-300 md:h-12">
                    <button
                      className="text-heading hover:bg-[#B88E2F] hover:text-white flex h-full w-10 flex-shrink-0 items-center justify-center border-e border-gray-300 transition duration-300 ease-in-out focus:outline-none md:w-12"
                      disabled={quantity === 1}
                      onClick={decrementQuantity}
                    >
                      -
                    </button>
                    <span className="duration-250 text-heading flex h-full w-10 flex-shrink-0 cursor-default items-center justify-center text-base font-semibold transition-colors ease-in-out  md:w-20 xl:w-24">
                      {quantity}
                    </span>
                    <button
                      className="text-heading hover:bg-[#B88E2F] hover:text-white flex h-full w-10 flex-shrink-0 items-center justify-center border-s border-gray-300 transition duration-300 ease-in-out focus:outline-none md:w-12"
                      onClick={incrementQuantity}
                    >
                      +
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    type="button"
                    onClick={() => handleCart(productDetails)}
                    className="h-12 w-full rounded-full bg-[#B88E2F] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#A67C2A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B88E2F]"
                  >
                    Add to cart
                  </button>
                </div>

                {/* Product Description */}
                <div className="pt-6 xl:pt-8">
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <h3 className="text-15px mb-3 font-semibold sm:text-base lg:mb-3.5">
                        Description:
                      </h3>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        {productDetails && productDetails.description}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </div>
              </div>
            </div>
          </div>

          {/* Snackbar for Alerts */}
          <Snackbar
            autoHideDuration={6000}
            isOpen={alertMsg.open}
            message={alertMsg.message}
            onClose={handleClose}
            placement="top"
            tone={alertMsg.severity}
          />
        </div>
      </div>
    </>
  );
};

export default SFProductDetails;
